/**
 * Générateur — Automatisme : l'aire sur quadrillage (carreaux de 1 cm)
 * Attendu ID : 1027 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « Dans des cas simples, l'élève
 * sait déterminer l'aire d'une surface en s'appuyant sur un quadrillage
 * composé de carreaux dont les côtés mesurent 1 cm. »
 * Labels (cartes exerciseur) :
 *   - L'aire sur quadrillage (carreaux de 1 cm)
 *
 * ── Plan de cartes (2026-08-21, validé prof) ───────────────────────────────
 * Ici on PRODUIT un nombre et une unité (contrairement au 1025 qui compare
 * sans mesurer). Deux formats sur la même banque de figures :
 *   - FIGURE (saisie, suffixe cm²), 4 familles progressives :
 *     ENTIERS ~35 % (rectangle, L rectiligne), DEMI-CARREAUX à 45° ~30 %
 *     (triangle isocèle rectangle, trapèze de pente 1 : les demis sont de
 *     VRAIS demis), MOITIÉ de rectangle ~20 % (le triangle de la flash,
 *     « ,5 » possible), CARRÉ INCLINÉ ~15 % (le cas le plus exigeant,
 *     minoritaire — « dans des cas simples » pris au sérieux) ;
 *   - QCM : mêmes figures, erreurs de comptage NOMMÉES et calculées
 *     analytiquement par famille (demis comptés entiers, demis oubliés,
 *     rectangle englobant, confusion périmètre, mal compté) ; plus un
 *     sous-type rare (~10 %) : « un demi-carreau représente quelle aire ? »
 * INVARIANTS : quadrillage visible (c'est l'énoncé), AUCUNE cote chiffrée
 * (on compte, on ne mesure pas), sommets sur les nœuds de la grille, aires
 * en demi-cm² ENTIERS (shoelace, garde qui throw).
 */
import {
    randomChoice, randInt,
    makeQcm, makeSaisie,
} from '../../../utils';

const ATTENDU_ID = 1027;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'L\'aire sur quadrillage (carreaux de 1 cm)';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1027 : ${message}`);
};

/** Aire × 2 (demi-carreaux) d'un polygone en unités de grille — entier. */
const aire2 = (pts) => {
    let somme = 0;
    for (let k = 0; k < pts.length; k++) {
        const [x1, y1] = pts[k];
        const [x2, y2] = pts[(k + 1) % pts.length];
        somme += x1 * y2 - x2 * y1;
    }
    garde(Number.isInteger(somme) && somme !== 0, 'aire invalide');
    return Math.abs(somme);
};

/** Demi-cm² entiers → écriture française (9 → « 4,5 », 12 → « 6 »). */
const fmt2 = (a2) => (a2 % 2 === 0 ? String(a2 / 2) : `${(a2 - 1) / 2},5`);

// ─── Les familles de figures (sommets sur les nœuds, pièges analytiques) ──

const FAMILLES = [
    {
        nom: 'entiers', poids: 35,
        tirer: () => {
            if (Math.random() < 0.5) {
                const L = randInt(3, 6); const h = randInt(2, 4);
                return {
                    pts: [[0, 0], [L, 0], [L, h], [0, h]],
                    pieges: [
                        { valeur2: 2 * 2 * (L + h), code: 'confusion_perimetre' },
                        { valeur2: 2 * (L * h + 1), code: 'mal_compte' },
                        { valeur2: 2 * (L * h - 1), code: 'mal_compte' },
                    ],
                };
            }
            const W = randInt(4, 6); const H = randInt(3, 4);
            const w = randInt(1, W - 2); const hn = randInt(1, H - 1);
            return {
                pts: [[0, 0], [W - w, 0], [W - w, hn], [W, hn], [W, H], [0, H]],
                pieges: [
                    { valeur2: 2 * W * H, code: 'rectangle_englobant' },
                    { valeur2: 2 * (W * H - w * hn + 1), code: 'mal_compte' },
                    { valeur2: 2 * (W * H - w * hn - 1), code: 'mal_compte' },
                ],
            };
        },
    },
    {
        nom: 'demis45', poids: 30,
        tirer: () => {
            if (Math.random() < 0.5) {
                // triangle isocèle rectangle : n(n−1)/2 entiers + n demis
                const n = randInt(2, 4);
                const E2 = n * (n - 1); const D = n;
                return {
                    pts: [[0, 0], [n, 0], [0, n]],
                    pieges: [
                        { valeur2: E2 + 2 * D, code: 'demis_comptes_entiers' },
                        { valeur2: E2, code: 'demis_oublies' },
                        { valeur2: n * n + 2, code: 'mal_compte' },
                    ],
                };
            }
            // trapèze rectangle de pente 1 : Lh + h(h−1)/2 entiers + h demis
            const L = randInt(2, 4); const h = randInt(2, 3);
            const E2 = 2 * L * h + h * (h - 1); const D = h;
            return {
                pts: [[0, 0], [L, 0], [L + h, h], [0, h]],
                pieges: [
                    { valeur2: E2 + 2 * D, code: 'demis_comptes_entiers' },
                    { valeur2: E2, code: 'demis_oublies' },
                    { valeur2: 2 * L * h + h * h + 2, code: 'mal_compte' },
                ],
            };
        },
    },
    {
        nom: 'moitie', poids: 20,
        tirer: () => {
            // le triangle de la flash : la MOITIÉ d'un rectangle L × h (L ≠ h)
            let L = randInt(3, 5); let h = randInt(2, 4);
            if (L === h) h = L - 1;
            return {
                pts: [[0, 0], [L, 0], [0, h]],
                pieges: [
                    { valeur2: 2 * L * h, code: 'rectangle_entier' },
                    { valeur2: L * h + 2, code: 'mal_compte' },
                    { valeur2: 2 * 2 * (L + h), code: 'confusion_perimetre' },
                ],
            };
        },
    },
    {
        nom: 'incline', poids: 15,
        tirer: () => {
            // le carré incliné (losange à 45°) : 2a(a−1) entiers + 4a demis
            const a = randomChoice([2, 2, 3]);
            const E2 = 4 * a * (a - 1); const D = 4 * a;
            return {
                pts: [[a, 0], [2 * a, a], [a, 2 * a], [0, a]],
                pieges: [
                    { valeur2: E2 + 2 * D, code: 'demis_comptes_entiers' },
                    { valeur2: E2, code: 'demis_oublies' },
                    { valeur2: 4 * a * a + 2, code: 'mal_compte' },
                ],
            };
        },
    },
];

const tirerFamille = () => {
    const total = FAMILLES.reduce((s, f) => s + f.poids, 0);
    let tirage = Math.random() * total;
    for (const f of FAMILLES) {
        tirage -= f.poids;
        if (tirage < 0) return f;
    }
    return FAMILLES[0];
};

// ─── Le dessin (quadrillage visible, aucune cote) ─────────────────────────

const U = 26;
const GRILLE = '#94a3b8';
const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const dessiner = (pts) => {
    const wU = Math.max(...pts.map((p) => p[0]));
    const hU = Math.max(...pts.map((p) => p[1]));
    garde(Math.min(...pts.map((p) => p[0])) >= 0 && Math.min(...pts.map((p) => p[1])) >= 0, 'figure non normalisée');
    // une marge d'un carreau de quadrillage autour de la figure
    const x0 = 20 + U; const y0 = 16 + U;
    const W = x0 + (wU + 1) * U + 20; const H = y0 + (hU + 1) * U + 16;
    garde(W <= 340 && H <= 260, 'figure trop grande');
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${Math.max(W, 220)} ${H}" width="${Math.max(W, 220)}" role="img"`
        + ' aria-label="Une figure sur un quadrillage dont les carreaux mesurent 1 cm de côté">';
    for (let gx = -1; gx <= wU + 1; gx++) {
        svg += `<line x1="${x0 + gx * U}" y1="${y0 - U}" x2="${x0 + gx * U}" y2="${y0 + (hU + 1) * U}" stroke="${GRILLE}" stroke-width="1"/>`;
    }
    for (let gy = -1; gy <= hU + 1; gy++) {
        svg += `<line x1="${x0 - U}" y1="${y0 + gy * U}" x2="${x0 + (wU + 1) * U}" y2="${y0 + gy * U}" stroke="${GRILLE}" stroke-width="1"/>`;
    }
    svg += `<polygon points="${pts.map(([x, y]) => `${x0 + x * U},${y0 + y * U}`).join(' ')}"`
        + ' fill="#1E3A8A" fill-opacity="0.4" stroke="#1E3A8A" stroke-width="2.6"/>';
    return dataUrl(`${svg}</svg>`);
};

// ─── Format 1 : la figure, en saisie ──────────────────────────────────────

const genFigureSaisie = () => {
    const famille = tirerFamille();
    const figure = famille.tirer();
    const a2 = aire2(figure.pts);
    const payload = makeSaisie(
        fmt2(a2),
        '<p><b>Quelle est l\'aire de cette figure ?</b> (les carreaux mesurent 1 cm de côté)</p>{{element:0}}',
        { ...opts(L1), suffixe: 'cm²', elements_additionnels: elImage(dessiner(figure.pts)) },
    );
    payload.contenu = { ...payload.contenu, sousType: famille.nom };
    payload._meta.params = { famille: famille.nom, aire2: a2 };
    return payload;
};

// ─── Format 2 : QCM à erreurs de comptage nommées ─────────────────────────

const genFigureQcm = () => {
    if (Math.random() < 0.1) {
        // le fait de la flash : un demi-carreau = 0,5 cm²
        const payload = makeQcm(
            ['0,5 cm²', '1 cm²', '2 cm²', '0,25 cm²'],
            '0,5 cm²',
            '<p><b>Un demi-carreau du quadrillage représente quelle aire ?</b></p>',
            opts(L1),
            false,
        );
        payload.contenu = { ...payload.contenu, sousType: 'demi_carreau' };
        payload._meta.params = {
            sousType: 'demi_carreau',
            distracteurs: [
                { valeur: '1 cm²', code: 'carreau_entier' },
                { valeur: '2 cm²', code: 'double' },
                { valeur: '0,25 cm²', code: 'quart' },
            ],
        };
        return payload;
    }
    for (let essai = 0; essai < 60; essai++) {
        const famille = tirerFamille();
        const figure = famille.tirer();
        const a2 = aire2(figure.pts);
        const bonne = `${fmt2(a2)} cm²`;
        const vus = new Set([a2]);
        const dis = figure.pieges.filter((p) => {
            if (!Number.isInteger(p.valeur2) || p.valeur2 <= 0 || vus.has(p.valeur2)) return false;
            vus.add(p.valeur2);
            return true;
        }).slice(0, 3);
        if (dis.length < 3) continue;
        const payload = makeQcm(
            [bonne, ...dis.map((p) => `${fmt2(p.valeur2)} cm²`)],
            bonne,
            '<p><b>Quelle est l\'aire de cette figure ?</b> (les carreaux mesurent 1 cm de côté)</p>{{element:0}}',
            { ...opts(L1), elements_additionnels: elImage(dessiner(figure.pts)) },
        );
        payload.contenu = { ...payload.contenu, sousType: famille.nom };
        payload._meta.params = { famille: famille.nom, aire2: a2, distracteurs: dis };
        return payload;
    }
    garde(false, 'QCM introuvable');
    return null;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quadrillage VISIBLE et aucune cote chiffrée : on compte, on ne mesure pas',
        'sommets sur les nœuds de la grille ; toutes les obliques sont des diagonales de carreaux (sauf l\'hypoténuse du sous-type moitié, traité par halving comme le corrigé flash)',
        'aires en demi-cm² ENTIERS (shoelace) ; erreurs calculées ANALYTIQUEMENT par famille, jamais au hasard',
    ],
    quadr_figure_qcm: {
        support: '4 familles progressives + le fait « demi-carreau = 0,5 cm² » (~10 %)',
        fixes: {
            demis_comptes_entiers: 'chaque demi-carreau compté comme un entier',
            demis_oublies: 'seuls les carreaux entiers comptés',
            rectangle_entier: 'le triangle compté comme son rectangle',
            rectangle_englobant: 'le L compté comme son rectangle englobant',
            confusion_perimetre: '2 × (L + l) : le périmètre au lieu de l\'aire',
            mal_compte: 'un carreau de trop ou de moins',
            carreau_entier: '1 cm² pour un demi-carreau',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    quadr_figure_saisie: genFigureSaisie,
    quadr_figure_qcm: genFigureQcm,
};

const FORMAT_LABELS = {
    quadr_figure_saisie: 'Saisie',
    quadr_figure_qcm: 'QCM',
};

export const CATEGORIES = [
    {
        id: 'quadr',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
